/**
 * Search component
 * Handles search functionality across the site
 */

import { api } from '../services/api.js';
import { 
  initFilters, 
  getActiveFilters, 
  filtersToURLParams, 
  buildDirectusFilter 
} from './filters.js';

/**
 * Initialize quick search on homepage
 */
export function initQuickSearch() {
  const searchInput = document.getElementById('quick-search');
  const searchBtn = document.getElementById('quick-search-btn');
  
  if (!searchInput || !searchBtn) return;
  
  // Handle search button click
  searchBtn.addEventListener('click', () => {
    const query = searchInput.value.trim();
    if (query) {
      window.location.href = `/search.html?q=${encodeURIComponent(query)}`;
    }
  });
  
  // Handle Enter key
  searchInput.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      const query = searchInput.value.trim();
      if (query) {
        window.location.href = `/search.html?q=${encodeURIComponent(query)}`;
      }
    }
  });
}

/**
 * Initialize full search page
 */
export async function initSearchPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const query = urlParams.get('q') || '';
  const type = urlParams.get('type') || '';
  const subjectId = urlParams.get('subject'); // Handle single subject parameter
  const page = parseInt(urlParams.get('page') || '1', 10);
  
  const searchInput = document.getElementById('search-input');
  const typeSelect = document.getElementById('type-select');
  const resultsContainer = document.getElementById('results-container');
  const loadingIndicator = document.getElementById('loading');
  const paginationContainer = document.getElementById('pagination');
  
  if (searchInput) {
    searchInput.value = query;
  }
  
  if (typeSelect) {
    typeSelect.value = type;
  }

  // Initialize filters first to handle subject parameter
  let initialFilters = {};
  if (subjectId) {
    // If we have a subject parameter, prepare it for the filters
    initialFilters = {
      subjects: [{ id: subjectId, name: '' }], // Name will be filled when filters load
      dateFrom: null,
      dateTo: null
    };
  }
  
  // Hide browse prompt and perform search if we have initial criteria
  const browsePrompt = document.getElementById('browse-prompt');
  if (query || type || subjectId) {
    if (browsePrompt) browsePrompt.classList.add('hidden');
    await performSearch(query, type, page, initialFilters, resultsContainer, loadingIndicator, paginationContainer);
  }
  
  // Initialize filters with callback for filter changes (after initial search)
  try {
    await initFilters((filters) => {
      handleFilterChange(filters, query, type, resultsContainer, loadingIndicator, paginationContainer);
    }, subjectId); // Pass subject ID to filters initialization
    
    // If we have a subject filter from URL but no query, trigger search with filters
    if (subjectId && !query && !type) {
      const filters = getActiveFilters();
      await performSearch('', '', 1, filters, resultsContainer, loadingIndicator, paginationContainer);
    }
    
    // Listen for filter change events (from filter removal)
    document.addEventListener('filterchange', () => {
      const filters = getActiveFilters();
      handleFilterChange(filters, query, type, resultsContainer, loadingIndicator, paginationContainer);
    });
  } catch (error) {
    console.warn('Filters initialization failed:', error);
    // Continue without filters
  }
  
  // Set up search form handler
  const searchForm = document.getElementById('search-form');
  if (searchForm) {
    searchForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const newQuery = searchInput.value.trim();
      const newType = typeSelect.value;

      if (newQuery || newType) {
        const filters = getActiveFilters();
        updateURL(newQuery, newType, 1, filters);
        await performSearch(newQuery, newType, 1, filters, resultsContainer, loadingIndicator, paginationContainer);
      }
    });
  }
  
  // Handle browser back/forward
  window.addEventListener('popstate', () => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q') || '';
    const t = params.get('type') || '';
    const p = parseInt(params.get('page') || '1', 10);
    
    if (searchInput) searchInput.value = q;
    if (typeSelect) typeSelect.value = t;
    
    if (q) {
      const filters = getActiveFilters();
      performSearch(q, t, p, filters, resultsContainer, loadingIndicator, paginationContainer);
    }
  });
}

/**
 * Handle filter change
 */
function handleFilterChange(filters, query, type, resultsContainer, loadingIndicator, paginationContainer) {
  // Allow search with just filters (no query required)
  const hasFilters = filters && (
    (filters.subjects && filters.subjects.length > 0) ||
    filters.dateFrom ||
    filters.dateTo
  );
  
  if (!query && !type && !hasFilters) {
    // All criteria cleared — restore browse prompt
    const browsePrompt = document.getElementById('browse-prompt');
    if (browsePrompt) browsePrompt.classList.remove('hidden');
    if (resultsContainer) resultsContainer.innerHTML = '';
    return;
  }

  // Update URL with new filters
  updateURL(query, type, 1, filters);

  // Perform search with new filters
  performSearch(query, type, 1, filters, resultsContainer, loadingIndicator, paginationContainer);
}

/**
 * Update URL with search parameters and filters
 */
function updateURL(query, type, page, filters) {
  const params = new URLSearchParams();
  params.set('q', query);
  if (type) params.set('type', type);
  params.set('page', page.toString());
  
  // Add filter parameters
  const filterParams = filtersToURLParams(filters);
  filterParams.forEach((value, key) => {
    params.set(key, value);
  });
  
  window.history.pushState({}, '', `?${params.toString()}`);
}

/**
 * Perform search and display results
 */
async function performSearch(query, type, page, filters, resultsContainer, loadingIndicator, paginationContainer) {
  if (!resultsContainer) return;
  
  // Check if we have any search criteria
  const hasFilters = filters && (
    (filters.subjects && filters.subjects.length > 0) ||
    filters.dateFrom ||
    filters.dateTo
  );
  
  if (!query && !type && !hasFilters) {
    const browsePrompt = document.getElementById('browse-prompt');
    if (browsePrompt) browsePrompt.classList.remove('hidden');
    return;
  }

  // Hide browse prompt when a search is running
  const browsePrompt = document.getElementById('browse-prompt');
  if (browsePrompt) browsePrompt.classList.add('hidden');

  const limit = 20;
  const offset = (page - 1) * limit;

  // Show loading
  if (loadingIndicator) {
    loadingIndicator.classList.remove('hidden');
  }
  resultsContainer.innerHTML = '';
  if (paginationContainer) {
    paginationContainer.classList.add('hidden');
  }
  
  // Scroll to top of results
  window.scrollTo({ top: 0, behavior: 'smooth' });
  
  try {
    // Build filter object for API
    const additionalFilters = filters && typeof buildDirectusFilter === 'function' 
      ? buildDirectusFilter(filters) 
      : [];
    
    const results = await api.search(query, { 
      type, 
      limit, 
      offset,
      additionalFilters 
    });
    
    // Hide loading
    if (loadingIndicator) {
      loadingIndicator.classList.add('hidden');
    }
    
    // Display results
    if (type) {
      // Single type results with pagination
      const items = results.data || [];
      displayResults(items, type, resultsContainer);
      
      // Show pagination if we have results
      if (items.length > 0 && paginationContainer) {
        displayPagination(query, type, page, items.length, limit, filters, paginationContainer);
      }
    } else {
      // Multi-type results (no pagination for multi-type)
      displayMultiTypeResults(results, resultsContainer);
    }
  } catch (error) {
    console.error('Search error:', error);
    if (loadingIndicator) {
      loadingIndicator.classList.add('hidden');
    }
    resultsContainer.innerHTML = '<p class="error">An error occurred while searching. Please try again.</p>';
  }
}

/**
 * Display results for a single content type
 */
function displayResults(items, type, container) {
  if (!items || items.length === 0) {
    container.innerHTML = '<p class="no-results">No results found. Try different search terms.</p>';
    return;
  }
  
  const resultsHTML = items.map(item => renderResultItem(item, type)).join('');
  container.innerHTML = `
    <div class="results-header">
      <h3>Found ${items.length} result${items.length !== 1 ? 's' : ''}</h3>
    </div>
    <div class="results-list">
      ${resultsHTML}
    </div>
  `;
}

/**
 * Display results for multiple content types
 */
function displayMultiTypeResults(results, container) {
  const types = ['papers', 'people', 'institutions', 'projects', 'datasets', 'events'];
  const totalResults = types.reduce((sum, type) => sum + (results[type]?.length || 0), 0);
  
  if (totalResults === 0) {
    container.innerHTML = '<p class="no-results">No results found. Try different search terms.</p>';
    return;
  }
  
  const sectionsHTML = types
    .filter(type => results[type] && results[type].length > 0)
    .map(type => {
      const items = results[type];
      const itemsHTML = items.map(item => renderResultItem(item, type)).join('');
      return `
        <section class="results-section">
          <h3>${capitalizeFirst(type)} (${items.length})</h3>
          <div class="results-list">
            ${itemsHTML}
          </div>
        </section>
      `;
    })
    .join('');
  
  container.innerHTML = `
    <div class="results-header">
      <h3>Found ${totalResults} result${totalResults !== 1 ? 's' : ''}</h3>
    </div>
    ${sectionsHTML}
  `;
}

/**
 * Render a single result item
 */
function renderResultItem(item, type) {
  // Map collection names to singular form for URL
  const typeMap = {
    'papers': 'paper',
    'people': 'person',
    'institutions': 'institution',
    'projects': 'project',
    'datasets': 'dataset',
    'events': 'event'
  };
  
  const singularType = typeMap[type] || type;
  const detailUrl = `/${singularType}.html?id=${item.id}`;
  
  switch (type) {
    case 'papers':
      const authors = formatAuthors(item.authors);
      return `
        <article class="result-item">
          <h4><a href="${detailUrl}">${escapeHtml(item.title)}</a></h4>
          ${authors ? `<p class="authors">${authors}</p>` : ''}
          ${item.abstract ? `<p class="excerpt">${escapeHtml(truncate(item.abstract, 200))}</p>` : ''}
          <div class="metadata">
            ${item.publication_date ? `<span class="date">${formatDate(item.publication_date)}</span>` : ''}
            ${item.publication_type ? `<span class="type">${escapeHtml(item.publication_type)}</span>` : ''}
          </div>
        </article>
      `;
    
    case 'people':
      const affiliations = formatAffiliations(item.affiliations);
      return `
        <article class="result-item">
          <h4><a href="${detailUrl}">${escapeHtml(item.name)}</a></h4>
          ${affiliations ? `<p class="affiliation">${affiliations}</p>` : ''}
          ${item.biography ? `<p class="excerpt">${escapeHtml(truncate(item.biography, 200))}</p>` : ''}
        </article>
      `;
    
    case 'institutions':
      return `
        <article class="result-item">
          <h4><a href="${detailUrl}">${escapeHtml(item.name)}</a></h4>
          ${item.type ? `<p class="type">${escapeHtml(item.type)}</p>` : ''}
          ${item.city && item.country ? `<p class="location">${escapeHtml(item.city)}, ${escapeHtml(item.country)}</p>` : ''}
          ${item.description ? `<p class="excerpt">${escapeHtml(truncate(item.description, 200))}</p>` : ''}
        </article>
      `;
    
    case 'projects':
      const investigators = formatInvestigators(item.investigators);
      // A funder the viewer cannot read (e.g. a draft, for the public) comes back as null
      const funders = (item.funding_institution || []).map(inst => inst.institutions_id).filter(Boolean);
      return `
        <article class="result-item">
          <h4><a href="${detailUrl}">${escapeHtml(item.title)}</a></h4>
          ${investigators ? `<p class="investigators">${investigators}</p>` : ''}
          ${item.focus ? `<p class="excerpt">${escapeHtml(truncate(item.focus, 200))}</p>` : ''}
          <div class="metadata">
            ${item.start_date ? `<span class="date">${formatDate(item.start_date)}</span>` : ''}
            ${funders.length > 0 ? `<span class="funding">${funders.map(inst => escapeHtml(inst.name)).join(', ')}</span>` : ''}
          </div>
        </article>
      `;
    
    case 'datasets':
      const creators = formatCreators(item.creators);
      return `
        <article class="result-item">
          <h4><a href="${detailUrl}">${escapeHtml(item.title)}</a></h4>
          ${creators ? `<p class="creators">${creators}</p>` : ''}
          ${item.description ? `<p class="excerpt">${escapeHtml(truncate(item.description, 200))}</p>` : ''}
          <div class="metadata">
            ${item.format ? `<span class="format">${escapeHtml(item.format)}</span>` : ''}
            ${item.license ? `<span class="license">${escapeHtml(item.license)}</span>` : ''}
          </div>
        </article>
      `;
    
    case 'events':
      return `
        <article class="result-item">
          <h4><a href="${detailUrl}">${escapeHtml(item.title)}</a></h4>
          ${item.event_type ? `<p class="type">${escapeHtml(item.event_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))}</p>` : ''}
          ${item.city || item.country ? `<p class="location">${[item.city, item.country].filter(Boolean).map(escapeHtml).join(', ')}${item.online ? ' (also online)' : ''}</p>` : item.online ? `<p class="location">Online</p>` : ''}
          ${item.description ? `<p class="excerpt">${escapeHtml(truncate(item.description, 200))}</p>` : ''}
          <div class="metadata">
            ${item.start_date ? `<span class="date">${formatDate(item.start_date)}</span>` : ''}
          </div>
        </article>
      `;

    default:
      return '';
  }
}

// Helper functions

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function truncate(text, length) {
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
}

function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatAuthors(authors) {
  if (!authors || authors.length === 0) return '';
  const authorNames = authors
    .map(a => a.people_id?.name)
    .filter(name => name); // Filter out null/undefined names
  
  if (authorNames.length === 0) return '';
  return authorNames.join(', ');
}

function formatAffiliations(affiliations) {
  if (!affiliations || affiliations.length === 0) return '';
  const affiliationNames = affiliations
    .map(a => a.institutions_id?.name)
    .filter(name => name); // Filter out null/undefined names
  
  if (affiliationNames.length === 0) return '';
  return affiliationNames.join(', ');
}

function formatInvestigators(investigators) {
  if (!investigators || investigators.length === 0) return '';
  const investigatorNames = investigators
    .map(i => i.people_id?.name)
    .filter(name => name); // Filter out null/undefined names
  
  if (investigatorNames.length === 0) return '';
  return investigatorNames.join(', ');
}

function formatCreators(creators) {
  if (!creators || creators.length === 0) return '';
  const creatorNames = creators
    .map(c => c.people_id?.name)
    .filter(name => name); // Filter out null/undefined names
  
  if (creatorNames.length === 0) return '';
  return creatorNames.join(', ');
}

/**
 * Display pagination controls
 */
function displayPagination(query, type, currentPage, resultCount, limit, filters, container) {
  if (!container) return;
  
  // If we got fewer results than the limit, we're on the last page
  const hasNextPage = resultCount === limit;
  const hasPrevPage = currentPage > 1;
  
  if (!hasNextPage && !hasPrevPage) {
    // No pagination needed
    container.classList.add('hidden');
    return;
  }
  
  container.classList.remove('hidden');
  
  const prevButton = hasPrevPage 
    ? `<button class="prev-page" data-page="${currentPage - 1}">← Previous</button>`
    : `<button class="prev-page" disabled>← Previous</button>`;
  
  const nextButton = hasNextPage
    ? `<button class="next-page" data-page="${currentPage + 1}">Next →</button>`
    : `<button class="next-page" disabled>Next →</button>`;
  
  container.innerHTML = `
    ${prevButton}
    <span class="page-info">Page ${currentPage}</span>
    ${nextButton}
  `;
  
  // Add click handlers
  const prevBtn = container.querySelector('.prev-page');
  const nextBtn = container.querySelector('.next-page');
  
  if (prevBtn && !prevBtn.disabled) {
    prevBtn.addEventListener('click', () => {
      navigateToPage(query, type, currentPage - 1, filters);
    });
  }
  
  if (nextBtn && !nextBtn.disabled) {
    nextBtn.addEventListener('click', () => {
      navigateToPage(query, type, currentPage + 1, filters);
    });
  }
}

/**
 * Navigate to a specific page
 */
function navigateToPage(query, type, page, filters) {
  updateURL(query, type, page, filters);
  
  // Trigger search
  const resultsContainer = document.getElementById('results-container');
  const loadingIndicator = document.getElementById('loading');
  const paginationContainer = document.getElementById('pagination');
  
  performSearch(query, type, page, filters, resultsContainer, loadingIndicator, paginationContainer);
}
